/* eslint-disable react-refresh/only-export-components */
import { cva, type VariantProps } from "class-variance-authority"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "cn"

const stepperIndicatorVariants = cva(
  "relative flex shrink-0 items-center justify-center [&>svg]:pointer-events-none",
  {
    variants: {
      variant: {
        prominent: "size-9 rounded-lg p-2 [&>svg]:size-5",
        default: "size-4 rounded-full",
      },
      status: {
        completed: "bg-neutrals-v2-100 text-brand-red-200",
        active: "bg-brand-red-200 text-neutrals-v2-100",
        inactive: "bg-neutrals-v2-500 text-neutrals-v2-100",
      },
    },
    defaultVariants: {
      variant: "prominent",
      status: "inactive",
    },
  }
)

export type StepperStatus = NonNullable<
  VariantProps<typeof stepperIndicatorVariants>["status"]
>
export type StepperVariant = NonNullable<
  VariantProps<typeof stepperIndicatorVariants>["variant"]
>

function Stepper({ className, ...props }: ComponentProps<"ol">) {
  return (
    <ol
      data-slot="stepper"
      className={cn("flex flex-col", className)}
      {...props}
    />
  )
}

export type StepperItemProps = ComponentProps<"li"> & {
  /** Figma-Achse „Status": abgeschlossener, aktueller oder zukünftiger Schritt. */
  status?: StepperStatus
  /** Figma-Achse „Variante": `prominent` (Icon-Box + Label) oder `default` (nur Punkt, ohne Label). */
  variant?: StepperVariant
  /** Nur bei `variant="prominent"` sichtbar. */
  icon?: ReactNode
}

/**
 * Der letzte Schritt blendet seine Verbindungslinie automatisch per
 * `:last-child` aus (`group-last/stepper-item`) — dafür braucht es keine
 * eigene `isLast`-Prop, ein `Stepper` muss lediglich alle `StepperItem`
 * direkt als Kinder rendern.
 */
function StepperItem({
  className,
  status = "inactive",
  variant = "prominent",
  icon,
  children,
  ...props
}: StepperItemProps) {
  return (
    <li
      data-slot="stepper-item"
      data-status={status}
      data-variant={variant}
      aria-current={status === "active" ? "step" : undefined}
      className={cn("group/stepper-item flex items-start gap-4", className)}
      {...props}
    >
      <div
        data-slot="stepper-indicator-column"
        className="flex flex-col items-center"
      >
        <span
          data-slot="stepper-indicator"
          className={cn(stepperIndicatorVariants({ variant, status }))}
        >
          {variant === "prominent" && icon}
        </span>
        {/* Feste Höhe statt flex-1: bei kurzem Label reicht die Zeilenhöhe sonst nicht aus, damit die Linie sichtbar wird (entspricht Figmas „Progress Line", 32px). */}
        <span
          data-slot="stepper-connector"
          className={cn(
            "h-8 w-0.5 rounded-full group-last/stepper-item:hidden",
            status === "completed" ? "bg-brand-red-200" : "bg-neutrals-v2-500"
          )}
        />
      </div>
      {children && (
        <div
          data-slot="stepper-label"
          className={cn(
            "flex min-w-0 flex-1 flex-col justify-center text-sm leading-5 text-labels-primary",
            variant === "prominent" ? "min-h-9" : "min-h-4",
            status === "active" ? "font-semibold" : "font-normal"
          )}
        >
          {children}
        </div>
      )}
    </li>
  )
}

export { Stepper, StepperItem, stepperIndicatorVariants }
